import {Inject, Injectable} from '@angular/core';
import {HttpClient, HttpHeaders} from '@angular/common/http';
import {ApiUrl} from '@emm/core/util/api-url.token';
import {Observable} from 'rxjs';
import {switchMap, take, tap} from 'rxjs/operators';
import {PublishedMenu} from '@emm/core/models/published-menu.model';

@Injectable()
export class MenuPublishingService {
    constructor(@Inject(ApiUrl) private apiUrl: string, private http: HttpClient) {}

    getPublishToken(): Observable<{ token: string, expiry: number }> {
        return this.http.post<{ token: string, expiry: number }>(`${this.apiUrl}/menu/publish/token`, {});
    }

    publishChainwide(consumer: string): Observable<PublishedMenu> {
        return this.getPublishToken().pipe(
            switchMap((token: { token: string, expiry: number }) => {
                const headers = new HttpHeaders({ 'Publish-Token': token.token });
                return this.http.post<PublishedMenu>(`${this.apiUrl}/menu/publish/consumer/${consumer}?forceOverride=true`, {},
                    { headers }).pipe();
            })
        );
    }

    publishChainwideDryRun(consumer: string): Observable<PublishedMenu> {
        return this.getPublishToken().pipe(
            switchMap((token: { token: string, expiry: number }) => {
                const headers = new HttpHeaders({ 'Publish-Token': token.token });
                return this.http.post<PublishedMenu>(`${this.apiUrl}/menu/publish/consumer/${consumer}?dryRun=true&forceOverride=true`, {},
                    { headers }).pipe();
            })
        );
    }
    publishNational(countryCode: string, consumer: string, includeStores?: boolean): Observable<PublishedMenu> {
        if (!includeStores){
            return this.getPublishToken().pipe(
                switchMap((token: { token: string, expiry: number }) => {
                    const headers = new HttpHeaders({'Publish-Token': token.token});
                    return this.http.post<PublishedMenu>(`${this.apiUrl}/menu/publish/national-menus/${countryCode}/${consumer}?forceOverride=true`,
                        {}, {headers}).pipe();
                })
            );
        } else {
            return this.getPublishToken().pipe(
                switchMap((token: { token: string, expiry: number }) => {
                    const headers = new HttpHeaders({ 'Publish-Token': token.token });
                    return this.http.post<PublishedMenu>(`${this.apiUrl}/menu/publish/location-set/${countryCode}/${consumer}?includeStores=true&forceOverride=true`,
                        {}, { headers }).pipe();
                })
            )
        }
    }

    publishNationalDryRun(countryCode: string, consumer: string, includeStores?: boolean): Observable<PublishedMenu> {
        if (!includeStores){
            return this.getPublishToken().pipe(
                switchMap((token: { token: string, expiry: number }) => {
                    const headers = new HttpHeaders({'Publish-Token': token.token});
                    return this.http.post<PublishedMenu>(`${this.apiUrl}/menu/publish/national-menus/${countryCode}/${consumer}?dryRun=true&forceOverride=true`,
                        {}, {headers}).pipe();
                })
            );
        } else {
            return this.getPublishToken().pipe(
                switchMap((token: { token: string, expiry: number }) => {
                    const headers = new HttpHeaders({ 'Publish-Token': token.token });
                    return this.http.post<PublishedMenu>(`${this.apiUrl}/menu/publish/location-set/${countryCode}/${consumer}?dryRun=true&includeStores=true&forceOverride=true`,
                        {}, { headers }).pipe();
                })
            )
        }
    }

    publishLocationSet(locationSetTag: string, consumer: string, includeStores?: boolean): Observable<PublishedMenu> {
        if (!includeStores) {
            return this.getPublishToken().pipe(
                switchMap((token: { token: string, expiry: number }) => {
                    const headers = new HttpHeaders({'Publish-Token': token.token});
                    return this.http.post<PublishedMenu>(`${this.apiUrl}/menu/publish/location-set/${locationSetTag}/${consumer}?forceOverride=true`,
                        {}, {headers}).pipe();
                })
            );
        } else {
            return this.getPublishToken().pipe(
                switchMap((token: { token: string, expiry: number }) => {
                    const headers = new HttpHeaders({'Publish-Token': token.token});
                    return this.http.post<PublishedMenu>(`${this.apiUrl}/menu/publish/location-set/${locationSetTag}/${consumer}?includeStores=true&forceOverride=true`,
                        {}, {headers}).pipe();
                })
            );
        }
    }

    publishLocationSetDryRun(locationSetTag: string, consumer: string, includeStores?: boolean): Observable<PublishedMenu> {
        if (!includeStores) {
            return this.getPublishToken().pipe(
                switchMap((token: { token: string, expiry: number }) => {
                    const headers = new HttpHeaders({'Publish-Token': token.token});
                    return this.http.post<PublishedMenu>(`${this.apiUrl}/menu/publish/location-set/${locationSetTag}/${consumer}?dryRun=true&forceOverride=true`,
                        {}, {headers}).pipe();
                })
            );
        } else {
            return this.getPublishToken().pipe(
                switchMap((token: { token: string, expiry: number }) => {
                    const headers = new HttpHeaders({'Publish-Token': token.token});
                    return this.http.post<PublishedMenu>(`${this.apiUrl}/menu/publish/location-set/${locationSetTag}/${consumer}?dryRun=true&includeStores=true&forceOverride=true`,
                        {}, {headers}).pipe();
                })
            );
        }
    }

    publishByStore(locationTag: string, consumer: string): Observable<PublishedMenu> {
        return this.http.post<PublishedMenu>(`${this.apiUrl}/menu/publish/location/${locationTag}/${consumer}?forceOverride=true`,
            {}).pipe();
    }

    publishByStoreDryRun(locationTag: string, consumer: string): Observable<PublishedMenu> {
        return this.http.post<PublishedMenu>(`${this.apiUrl}/menu/publish/location/${locationTag}/${consumer}?dryRun=true&forceOverride=true`,
            {}).pipe();
    }

    publishVenue(venueId): Observable<PublishedMenu> {
        return this.http.post<PublishedMenu>(`${this.apiUrl}/menu/publish/venue/${venueId}?forceOverride=true`,
            {}).pipe();

    }

    publishVenueDryRun(venueId): Observable<PublishedMenu> {
        return this.http.post<PublishedMenu>(`${this.apiUrl}/menu/publish/venue/${venueId}?dryRun=true&forceOverride=true`,
            {}).pipe();
    }
}
